import { Injectable } from '@angular/core'
import * as Sentry from '@sentry/browser'

import { UserState } from '../@store'
import { constants } from '../../environments/environment'
import { State } from '../@store/reducers/release-info.reducer'

@Injectable({
  providedIn: 'root',
})
export class SentryContextService {
  user: UserState | null = null

  static didInitSentry() {
    return Boolean(Sentry.getCurrentHub().getClient())
  }

  static reportMessage(message: string, severity: Sentry.Severity = Sentry.Severity.Info): void {
    if (!SentryContextService.didInitSentry()) {
      console.warn('Trying to report message to Sentry before initialization. This message will be ignored', {
        message,
        severity,
        this: this,
      })
      return
    }
    Sentry.captureMessage(message, severity)
  }

  setUser(user: UserState): void {
    // Avoid setting the same user twice.
    if (this.user && user.id === this.user.id) {
      return
    }
    this.user = user
    Sentry.setUser({
      id: user.id.toString(),
      email: user.email,
      name: user.name,
      roles: user.roles,
    })
  }

  setReleaseVersions(versions: State): void {
    Sentry.configureScope(function setExtraScopes(scope) {
      scope.setExtras({
        versions,
        mapsApiVersion: window.google?.maps?.version ?? 'Missing google.maps.version',
      })
    })
  }

  reportError(error: Partial<Error>): void {
    if (!SentryContextService.didInitSentry()) {
      console.warn('Trying to report error to Sentry before initialization. This message will be ignored', {
        error,
        this: this,
      })
      return
    }
    const reportedError = error instanceof Error ? error : Error(error.message)
    if (error.stack) {
      reportedError.stack = error.stack
    }
    Sentry.captureException(reportedError)
  }
}

export function initSentry(): void {
  // @ts-ignore when running with a non local environment, TS complains about a constant `false` valued expression
  if (constants.sentryUrl === '' || constants.envName === 'dev') {
    console.debug('Skipping Sentry initialization in dev.', { constants })
    return
  }
  Sentry.init({
    dsn: constants.sentryUrl,
    environment: `operator_ui_${constants.envName}`,
    release: constants.version,
    ignoreErrors: [
      'Non-Error exception captured', // Internal Sentry error we don't care about
    ],
    debug: constants.envName !== 'production',
  })
  console.debug('Initialized Sentry.io SDK.')
}
